import { ActionIcon, Table, Text } from '@mantine/core';
import type { KeyboardEvent } from 'react';
import { textOrDash } from '../../../../../../../shared/utils/formatText';
import { formatDate } from '../../../../../../../shared/utils/time';
import { AS_CS_RECEIPT_STATUS_LABELS } from '../../../../../../../shared/constants/asCsOptions';
import { IconSearch } from '@tabler/icons-react';
import type { IAsCsProcessDetail } from '../types';

interface IProcessHistoryTableProps {
  rows: IAsCsProcessDetail[];
  totalCount: number;
  page: number;
  size: number;
  isLoading?: boolean;
  isError?: boolean;
  onSelectProcess?: (processId: number) => void;
}

export default function ProcessHistoryTable(props: IProcessHistoryTableProps) {
  const { rows, totalCount, page, size, isLoading, isError, onSelectProcess } = props;
  const canSelectProcess = Boolean(onSelectProcess);

  const handleRowKeyDown = (event: KeyboardEvent<HTMLTableRowElement>, processId: number) => {
    if (!onSelectProcess || (event.key !== 'Enter' && event.key !== ' ')) {
      return;
    }

    event.preventDefault();
    onSelectProcess(processId);
  };

  return (
    <Table.ScrollContainer minWidth={768}>
      <Table highlightOnHover={canSelectProcess}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>번호</Table.Th>
            <Table.Th>처리일시</Table.Th>
            <Table.Th>변경자</Table.Th>
            <Table.Th>조치 내용</Table.Th>
            <Table.Th>진행 상태</Table.Th>
            <Table.Th>상세 보기</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {!isLoading &&
            !isError &&
            rows.map((row, index) => (
              <Table.Tr
                key={row.id}
                role={canSelectProcess ? 'button' : undefined}
                tabIndex={canSelectProcess ? 0 : undefined}
                aria-label={canSelectProcess ? '처리 상세 보기' : undefined}
                onClick={onSelectProcess ? () => onSelectProcess(row.id) : undefined}
                onKeyDown={(event) => handleRowKeyDown(event, row.id)}
                style={{
                  cursor: canSelectProcess ? 'pointer' : undefined,
                }}
              >
                <Table.Td style={{ whiteSpace: 'nowrap' }}>{totalCount - ((page - 1) * size + index)}</Table.Td>
                <Table.Td>{formatDate(row.processDt)}</Table.Td>
                <Table.Td>{textOrDash(row.processManagerName)}</Table.Td>
                <Table.Td maw={320} ta={row.processContent ? 'start' : 'center'}>
                  <Text size="sm" truncate="end">
                    {textOrDash(row.processContent)}
                  </Text>
                </Table.Td>
                <Table.Td style={{ whiteSpace: 'nowrap' }}>
                  {AS_CS_RECEIPT_STATUS_LABELS[row.processStatus as keyof typeof AS_CS_RECEIPT_STATUS_LABELS] ?? '-'}
                </Table.Td>
                <Table.Td style={{ whiteSpace: 'nowrap' }}>
                  <ActionIcon variant="default">
                    <IconSearch size={16} />
                  </ActionIcon>
                </Table.Td>
              </Table.Tr>
            ))}
          {(isLoading || isError || !rows.length) && (
            <Table.Tr>
              <Table.Td colSpan={6}>
                <Text ta="center" c={isError ? 'red' : 'dimmed'} py="md" size="sm">
                  {isLoading ? '불러오는 중입니다.' : isError ? 'AS/CS 정보를 불러오지 못했습니다.' : '검색 결과가 없습니다.'}
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}
